import type { ProductListingState } from '@/components/pages/product-listing-v2/types';

export interface ResolveListingStateArgs {
  /** EWM-184 — dealer's organisation has no Product Selection assigned at all. */
  noProductSelection?: boolean;
  /**
   * EWM-855 — the category is real (the BFF resolved it, the page is a 200) but
   * holds nothing in this dealer's selection, so the tastic deliberately skipped
   * the Algolia fetch.
   */
  categoryOutOfSelection?: boolean;
  /** False when the server tastic produced no Algolia payload. */
  hasAlgoliaData: boolean;
  /** Number of hits in that payload. */
  hitCount: number;
  /** `/search` renders different zero-result copy than a category browse. */
  isSearchPage: boolean;
  /**
   * Total active refinements — distinguishes "nothing here" from "you filtered
   * it down to nothing", which is what decides whether the zero-result state
   * offers a Clear Filters action.
   *
   * EWM-686 widened this from brand-only. It now counts every refinement the
   * customer can apply — brands, categories, dynamic attribute facets and the
   * boolean quick filters (e.g. "Show in-stock only") — because a listing
   * emptied by ANY of them needs the same one-action undo. Previously ticking a
   * non-brand filter to zero results dropped a category PLP to `empty-category`,
   * which renders no CTA at all, leaving the customer stuck.
   */
  activeFilterCount: number;
}

/**
 * Resolves which page state the PLP / `/search` view renders.
 *
 * Extracted from the view-model so the branches are testable without mounting a
 * component wired to a dozen hooks. That mattered: the EWM-855 bug lived here and
 * no test could reach it — a dealer hitting a category outside their selection
 * fell through to `error` and was shown a network-failure message plus a Retry
 * button that could never succeed.
 *
 * ORDER IS LOAD-BEARING. Both `no-product-selection` and `empty-category`
 * (out-of-selection) must be decided BEFORE the `hasAlgoliaData` guard, because
 * all three present identically as "no Algolia payload" and only the flags
 * distinguish an intended empty result from a genuine backend failure.
 */
export const resolveListingState = ({
  noProductSelection,
  categoryOutOfSelection,
  hasAlgoliaData,
  hitCount,
  isSearchPage,
  activeFilterCount,
}: ResolveListingStateArgs): ProductListingState => {
  if (noProductSelection) return 'no-product-selection';
  if (categoryOutOfSelection) return 'empty-category';
  if (!hasAlgoliaData) return 'error';
  if (hitCount > 0) return 'ready';
  // EWM-686 — a listing the customer filtered down to nothing gets `no-matches`
  // (which carries the Clear Filters CTA) on EVERY surface, not just /search.
  // Without filters it stays as before: the query-specific copy on /search, the
  // empty-category message on a category or collection page.
  if (activeFilterCount > 0) return 'no-matches';
  return isSearchPage ? 'no-matches-query' : 'empty-category';
};
